import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";

const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M13.5 21v-7.5h2.5l.5-3h-3V8.6c0-.9.25-1.5 1.55-1.5H17V4.4c-.3-.05-1.35-.15-2.55-.15-2.5 0-4.2 1.55-4.2 4.35v2.4H7.75v3h2.5V21h3.25z" />
  </svg>
);

const InstagramIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();

  const exploreItems = [
    { key: "home", to: "/" },
    {
      key: "gear",
      group: true,
      children: [
        { key: "rifleSystems", to: "/riffle-systems" },
        { key: "apparel", to: "/apparel" },
      ],
    },
    { key: "experience", to: "/experience" },
    { key: "about", to: "/about" },
  ];

  const socials = [
    { Icon: FacebookIcon, href: "#", label: "Facebook" },
    {
      Icon: InstagramIcon,
      href: "https://www.instagram.com/tajikistan.trophy.hunt/",
      label: "Instagram",
    },
  ];

  return (
    <footer
      style={{ backgroundColor: "#223746" }}
      className="text-white relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-600/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-20 pb-8">
        <div className="flex flex-col items-center text-center mb-14 sm:mb-20">
          <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-yellow-600 mb-4">
            {t("footer.kicker")}
          </p>
          <h2 className="text-3xl sm:text-5xl font-bold tracking-[0.2em]">
            {t("common.brand")}
          </h2>
          <div className="mt-6 h-px w-16 bg-yellow-600/70" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 mb-14">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-600 mb-6">
              {t("footer.explore.title")}
            </h3>
            <ul className="space-y-3">
              {exploreItems.map((item) =>
                item.group ? (
                  <li key={item.key}>
                    <span className="block text-sm font-semibold text-white uppercase tracking-wider mb-2">
                      {t(`footer.explore.items.${item.key}`)}
                    </span>
                    <ul className="space-y-2 pl-4 border-l border-white/10">
                      {item.children.map((child) => (
                        <li key={child.key}>
                          <Link
                            to={child.to}
                            className="group inline-flex items-center text-sm text-gray-300 hover:text-white transition-colors duration-200"
                          >
                            <span className="w-0 group-hover:w-4 h-px bg-yellow-600 mr-0 group-hover:mr-3 transition-all duration-300" />
                            {t(`footer.explore.items.${child.key}`)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      className="group inline-flex items-center text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-yellow-600 mr-0 group-hover:mr-3 transition-all duration-300" />
                      {t(`footer.explore.items.${item.key}`)}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="md:border-l md:border-r md:border-white/10 md:px-10">
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-600 mb-6">
              {t("footer.company.title")}
            </h3>
            <ul className="space-y-4 text-sm text-gray-300">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-yellow-600 mt-0.5 shrink-0" />
                <span>
                  {t("footer.company.addressLine1")}
                  <br />
                  {t("footer.company.addressLine2")}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-yellow-600 mt-0.5 shrink-0" />
                <a
                  href="mailto:sales@shohin-m.com"
                  className="hover:text-white transition-colors duration-200"
                >
                  sales@shohin-m.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-yellow-600 mt-0.5 shrink-0" />
                <div className="flex flex-col">
                  <a
                    href="tel:+992909911111"
                    className="hover:text-white transition-colors duration-200"
                  >
                    +992 909 911 111
                  </a>
                  <a
                    href="tel:+992999777000"
                    className="hover:text-white transition-colors duration-200"
                  >
                    +992 999 777 000
                  </a>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-600 mb-6">
              {t("footer.follow.title")}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              {t("footer.follow.body")}
            </p>
            <div className="flex gap-3">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="w-10 h-10 flex items-center justify-center border border-white/15 hover:border-yellow-600 hover:text-yellow-600 text-gray-300 transition-colors duration-200"
                >
                  <Icon width={16} height={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-gray-400 tracking-wide">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
